import {
  ErrorState,
  Field,
  FieldCheckbox,
  FieldInput,
  FieldLabel,
  FieldSelect,
  FieldTextarea,
  LoadingState,
} from '@ibe/ui';
import { Suspense, lazy, useMemo, type ReactNode } from 'react';
import {
  Controller,
  type Control,
  type FieldErrors,
  type UseFormRegister,
  useWatch,
} from 'react-hook-form';

import { PropertySelect } from '../../../components/form/property-select.js';
import { useNotificationTemplateTags } from '../api/queries.js';
import { extractTemplateVariables } from '../api/variables.js';
import {
  CHANNEL,
  CHANNEL_OPTIONS,
  TEMPLATE_TYPE_OPTIONS,
  type NotificationTemplateFormInput,
} from '../api/schemas.js';

// Jodit is used only on the email template form screens. Lazy-loading it keeps
// the editor and its stylesheet out of the initial bundle.
const RichTextEditor = lazy(async () => {
  const [{ RichTextEditor: Editor }] = await Promise.all([
    import('../../../components/form/rich-text-editor.js'),
  ]);
  return { default: Editor };
});

/**
 * A notification template's inputs.
 *
 * Email and SMS are one form because the differences are small: only email has
 * a subject, a from address and a from name. The body switches between a rich
 * HTML editor and a plain textarea depending on the channel.
 */

export interface NotificationTemplateFieldsProps {
  readonly register: UseFormRegister<NotificationTemplateFormInput>;
  readonly errors: FieldErrors<NotificationTemplateFormInput>;
  readonly control: Control<NotificationTemplateFormInput>;
  readonly mode: 'create' | 'edit';
}

function Section({
  title,
  description,
  children,
}: {
  readonly title: string;
  readonly description?: string;
  readonly children: ReactNode;
}): React.JSX.Element {
  return (
    <fieldset className="space-y-4 border-t border-border pt-4 first:border-t-0 first:pt-0">
      <legend className="sr-only">{title}</legend>
      <div className="space-y-1">
        <h2 className="text-body font-semibold">{title}</h2>
        {description === undefined ? null : (
          <p className="text-caption text-muted-foreground">{description}</p>
        )}
      </div>
      {children}
    </fieldset>
  );
}

function Row({ children }: { readonly children: ReactNode }): React.JSX.Element {
  return <div className="grid grid-cols-1 gap-4 md:grid-cols-2">{children}</div>;
}

export function NotificationTemplateFields({
  register,
  errors,
  control,
  mode,
}: NotificationTemplateFieldsProps): React.JSX.Element {
  const channel = useWatch({ control, name: 'channel' });
  const body = useWatch({ control, name: 'body' });
  const isEmail = channel === CHANNEL.EMAIL;
  const variables = useMemo(() => extractTemplateVariables(body), [body]);
  const tagsQuery = useNotificationTemplateTags();

  return (
    <div className="space-y-6">
      <Section
        title="Identity"
        description="A template belongs to a tenant or a single property, and is picked by a channel, type and code."
      >
        <Controller
          control={control}
          name="channel"
          render={({ field }) => (
            <Field error={errors.channel?.message} required>
              <FieldLabel>Channel</FieldLabel>
              <FieldSelect
                value={field.value}
                onValueChange={field.onChange}
                options={CHANNEL_OPTIONS}
                placeholder="Choose a channel"
                searchable
              />
            </Field>
          )}
        />

        <Controller
          control={control}
          name="propertyId"
          render={({ field }) => (
            <PropertySelect
              value={field.value}
              onChange={field.onChange}
              error={errors.propertyId?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="code"
          render={({ field }) => (
            <Field error={errors.code?.message} required>
              <FieldLabel>Template code / notification type</FieldLabel>
              <FieldSelect
                value={field.value}
                onValueChange={field.onChange}
                options={TEMPLATE_TYPE_OPTIONS}
                placeholder="Choose a template code"
                disabled={mode === 'edit'}
                searchable
              />
            </Field>
          )}
        />

        <Field error={errors.name?.message} required>
          <FieldLabel>Template name</FieldLabel>
          <FieldInput
            autoComplete="off"
            placeholder="Booking confirmation email"
            {...register('name')}
          />
        </Field>
      </Section>

      <Section
        title="Message"
        description="Use {{variableName}} to insert values at send time. The editor's preview shows the final guest-facing layout."
      >
        {isEmail ? (
          <Field error={errors.subject?.message} required>
            <FieldLabel>Subject</FieldLabel>
            <FieldInput autoComplete="off" {...register('subject')} />
          </Field>
        ) : null}

        {isEmail ? (
          <Row>
            <Field error={errors.fromAddress?.message}>
              <FieldLabel>From address</FieldLabel>
              <FieldInput
                autoComplete="off"
                placeholder="noreply@example.com"
                {...register('fromAddress')}
              />
            </Field>

            <Field error={errors.fromName?.message}>
              <FieldLabel>From name</FieldLabel>
              <FieldInput autoComplete="off" placeholder="Name" {...register('fromName')} />
            </Field>
          </Row>
        ) : null}

        <Field error={errors.body?.message} required>
          <FieldLabel>{isEmail ? 'Email body' : 'SMS body'}</FieldLabel>
          {isEmail ? (
            <Controller
              control={control}
              name="body"
              render={({ field }) => (
                <Suspense fallback={<LoadingState label="Loading editor" />}>
                  <RichTextEditor
                    label="Email body"
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.body?.message}
                    description="Use {{name}} for variables. Use the editor's Preview to see how it will look."
                  />
                </Suspense>
              )}
            />
          ) : (
            <FieldTextarea rows={6} {...register('body')} />
          )}
        </Field>

        {variables.length > 0 ? (
          <Field>
            <FieldLabel>Variables detected</FieldLabel>
            <div className="rounded-md border border-border bg-muted p-3 text-sm text-muted-foreground">
              <code>{variables.join(', ')}</code>
            </div>
          </Field>
        ) : null}
      </Section>

      <Section
        title="Defaults"
        description="Only one template per type can be the default in each scope."
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field>
            <Controller
              control={control}
              name="isDefault"
              render={({ field }) => (
                <FieldCheckbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  onBlur={field.onBlur}
                >
                  Default for this type
                </FieldCheckbox>
              )}
            />
          </Field>

          <Field>
            <Controller
              control={control}
              name="isActive"
              render={({ field }) => (
                <FieldCheckbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  onBlur={field.onBlur}
                >
                  Active
                </FieldCheckbox>
              )}
            />
          </Field>
        </div>
      </Section>

      <Section
        title="Notification tags"
        description="Copy a replacement tag into the body to insert booking, guest or property values at send time."
      >
        {tagsQuery.isPending ? (
          <LoadingState label="Loading notification tags" />
        ) : tagsQuery.error !== null ? (
          <ErrorState
            description="Could not load notification tags."
            onRetry={() => {
              void tagsQuery.refetch();
            }}
          />
        ) : (
          <div className="max-h-96 overflow-auto rounded-md border border-border">
            <table className="w-full border-collapse text-sm">
              <thead className="bg-muted sticky top-0">
                <tr className="text-left">
                  <th className="py-2 px-3 font-medium">Field Title</th>
                  <th className="py-2 px-3 font-medium">Field Name</th>
                  <th className="py-2 px-3 font-medium">Replacement Tag</th>
                </tr>
              </thead>
              <tbody>
                {tagsQuery.data.map((tag) => (
                  <tr key={tag.id} className="border-t">
                    <td className="py-2 px-3">{tag.fieldTitle}</td>
                    <td className="py-2 px-3">
                      <code>{tag.fieldName}</code>
                    </td>
                    <td className="py-2 px-3">
                      <code className="text-primary">{tag.replacementTag}</code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}
